import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningIcon from '@mui/icons-material/Warning';
import { GridRenderCellParams, GridColDef } from '@mui/x-data-grid-premium';
import { FakeButtonLink } from '../../components/FakeLink';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { useSearchParamsState } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import { cleanBatchExecutions, fetchBatchExecutions } from '../../redux/modules/batchExecution/batchExecution.actions';
import { batchExecutionByRequestIdSelector } from '../../redux/modules/batchExecution/batchExecution.selectors';
import { ReducerState } from '../../redux/store.types';
import { locations } from '../../routes/locations';
import { FeeBatchExecutionStatus } from '../../types/feeBatch';
import { parseJSON } from '../../utils/json';

const batchExecutionStatusToInfo: {
  [prop in FeeBatchExecutionStatus]: {
    color: 'error' | 'info' | 'success' | 'warning';
    icon: React.ElementType;
  };
} = {
  [FeeBatchExecutionStatus.EXECUTION]: {
    color: 'info',
    icon: InfoIcon,
  },
  [FeeBatchExecutionStatus.APPROVED]: {
    color: 'success',
    icon: CheckCircleIcon,
  },
  [FeeBatchExecutionStatus.IN_REVIEW]: {
    color: 'warning',
    icon: WarningIcon,
  },
  [FeeBatchExecutionStatus.RECONCILIATION]: {
    color: 'warning',
    icon: WarningIcon,
  },
};

type BatchExecutionTableProps = {
  executionId: string;
};

export function BatchExecutionTable({ executionId }: BatchExecutionTableProps) {
  const requestId = `batch-executions-list-by-execution-id-${executionId}`;
  const dispatch = useAppDispatch();
  const tableRef = useRef<TableRef>(null);
  const [columns] = useState<GridColDef[]>([
    {
      field: 'id',
      headerName: 'Id',
      width: 80,
    },
    {
      field: 'executionId',
      headerName: 'Execution Id',
      type: 'string',
      flex: 1,
    },
    {
      field: 'batchId',
      headerName: 'BatchId',
      type: 'string',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return;
        }

        return (
          <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.feeBatch(params.value)}>
            {params.value}
          </FakeButtonLink>
        );
      },
    },
    {
      field: 'status.name',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: Object.values(FeeBatchExecutionStatus),
      minWidth: 150,
      valueGetter: (params) => params.row?.status?.name,
      renderCell: (params: GridRenderCellParams) => {
        const info = batchExecutionStatusToInfo[params.row?.status?.name as FeeBatchExecutionStatus] || {};
        const Icon = info?.icon;

        if (!Icon) {
          return params.row?.status?.name;
        }

        return <Chip variant="outlined" color={info?.color} icon={<Icon />} label={params.row?.status?.name} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
  ]);
  const { totalbatchExecutions, batchExecutions, fetchStatus } = useSelector((state: ReducerState) =>
    batchExecutionByRequestIdSelector(state, requestId),
  );
  const loading = fetchStatus === fetchStatuses.pending;
  const [initialFilterModel, setInitialFilterModel] = useSearchParamsState('filter', '');
  const [initialPage, setInitialPage] = useSearchParamsState('page', '0');
  const [initialPageSize, setInitialPageSize] = useSearchParamsState('pageSize', '50');

  useEffect(
    () => () => {
      dispatch(cleanBatchExecutions());
    },
    [],
  );

  return (
    <>
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">Batch Executions &nbsp;</Typography>
      </Box>
      <Table
        ref={tableRef}
        wrapperHeight={300}
        fetchItems={fetchBatchExecutions}
        rows={batchExecutions}
        columns={columns}
        loading={loading}
        disableColumnFilter
        disableColumnMenu
        density="compact"
        rowCount={totalbatchExecutions}
        initialFilterModel={parseJSON(initialFilterModel)}
        initialPage={+initialPage}
        initialPageSize={+initialPageSize}
        fetchItemsCustomParams={{ requestId }}
        requiredFilters={[{ field: 'executionId', value: executionId, operator: 'is', id: 0 }]}
        initialColumnVisibilityModel={{ id: false, executionId: false }}
        onFilterModelChange={(filterModel) => setInitialFilterModel(JSON.stringify(filterModel))}
        onPageChange={(page) => setInitialPage(`${page}`)}
        onPageSizeChange={(pageSize) => setInitialPageSize(`${pageSize}`)}
      />
    </>
  );
}
